import { parseServerDate } from './parseServerDate';

export const DateFormat = {
  /** DD/MM/YY — e.g. "27/05/26" */
  NUMERIC: 'numeric',
  /** Month D, YYYY — e.g. "May 27, 2026" */
  WRITTEN: 'written',
  /** Month D, YYYY at H:MM AM/PM UTC — e.g. "July 7, 2026 at 10:13 AM UTC" */
  WRITTEN_DATETIME: 'written_datetime',
  /** Weekday, Month D — e.g. "Tuesday, September 1". No year: for dates within the next month. */
  WRITTEN_WEEKDAY: 'written_weekday',
  /** Mon D — e.g. "May 27". No year: for a table column header or a chart axis tick, where space is short. */
  SHORT: 'short'
} as const;

export type DateFormat = (typeof DateFormat)[keyof typeof DateFormat];

/**
 * Formats a date string for display.
 * 'en-GB' for NUMERIC is intentional — consistent DD/MM/YY output regardless of user locale.
 */
export const formatDate = (iso: string, format: DateFormat = DateFormat.NUMERIC): string => {
  const date = parseServerDate(iso);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  if (format === DateFormat.WRITTEN) {
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC'
    }).format(date);
  }

  if (format === DateFormat.SHORT) {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(date);
  }

  if (format === DateFormat.WRITTEN_WEEKDAY) {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC'
    }).format(date);
  }

  if (format === DateFormat.WRITTEN_DATETIME) {
    const parts = new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC'
    }).formatToParts(date);

    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';

    return `${get('month')} ${get('day')}, ${get('year')} at ${get('hour')}:${get('minute')} ${get('dayPeriod')} UTC`;
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    timeZone: 'UTC'
  }).format(date);
};

/** Milliseconds in one day. */
export const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Formats a date as an RFC 3339 UTC timestamp without milliseconds, e.g. "2026-01-01T00:00:00Z" —
 * the form the Manage API v2 contracts use for `from` / `to` query params.
 * @param date - The moment to format.
 */
export const formatRfc3339 = (date: Date) => date.toISOString().replace(/\.\d{3}Z$/, 'Z');

/**
 * Midnight UTC of the date's UTC calendar day, as a timestamp.
 * @param date - The moment whose day starts the result.
 */
export const startOfUtcDay = (date: Date) => Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

/**
 * Number of days in a UTC month. `month` is zero-based and may overflow or underflow the year, as in `Date.UTC`.
 * @param params.year - The full year.
 * @param params.month - The zero-based month.
 */
export const daysInUtcMonth = ({ year, month }: { year: number; month: number }) =>
  new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

/**
 * The same UTC time `months` calendar months earlier, as a timestamp. A day the target month doesn't have
 * becomes that month's last day, so 31 May minus three months is 28 February, not 3 March.
 * @param params.date - The moment to count back from.
 * @param params.months - How many calendar months to go back.
 */
export const subtractUtcMonths = ({ date, months }: { date: Date; months: number }) => {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() - months;
  const day = Math.min(date.getUTCDate(), daysInUtcMonth({ year, month }));

  return Date.UTC(
    year,
    month,
    day,
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds()
  );
};
